import { useCallback, useEffect, useRef, useState } from 'react'
import Sidebar from '../components/Sidebar'
import {
  UiVersionCell,
  UiVersionFilter,
  type UiVersionFilterValue,
} from '../components/admin/UiVersionControls'
import { api, ApiRequestError } from '../lib/api'
import { relativeTime, absoluteTime } from '../lib/relativeTime'
import { toUiVersion, type UiVersion } from '../stores/authStore'
import {
  useVocabularyStore,
  type VocabTerm,
  type VocabCandidate,
  type VocabFilter,
} from '../stores/vocabularyStore'

type AdminTab = 'users' | 'invites' | 'effectiveness' | 'vocabulary'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdminUser {
  id: string
  email: string
  display_name: string | null
  is_admin: boolean
  account_status: string
  api_key_mode: string | null
  ui_version?: string
  joined_at: string
  last_login_at: string | null
  monthly_cap_usd: number | null
  cv_count_this_month: number
  spend_this_month_usd: number
  spend_all_time_usd: number
}

interface AdminUsersResponse {
  users: AdminUser[]
  month: number
  year: number
  total_spend_this_month: number
  active_user_count: number
}

interface Invite {
  id: string
  label: string
  api_key_mode_granted: string
  monthly_cap_usd: number | null
  max_uses: number
  use_count: number
  status: string
  created_at: string
  expires_at: string
  accepted_at: string | null
  invite_url: string | null
}

interface EffectivenessEntry {
  user_id: string
  email: string
  cv_count: number
  avg_iterations: number
  avg_questions: number
  last_active: string | null
}

interface EffectivenessResponse {
  entries: EffectivenessEntry[]
  avg_iterations_overall: number
  avg_questions_overall: number
  cvs_approved_this_month: number
  month: number
  year: number
}

type FactClass = 'authoritative' | 'best_effort' | 'potentially_stale'

interface UserJourney {
  user: {
    id: string
    email: string
    display_name: string | null
    account_status: string
    api_key_mode: string | null
    joined_at: string
    last_login_at: string | null
  }
  summary: string
  onboarding: {
    completed: boolean
    completed_at: string | null
    completed_at_confidence: string
    status: string | null
    current_step: string | null
    copy: string
  }
  library: { active: number; needs_review: number; archived: number; discarded: number }
  cv_generation: {
    exists: boolean
    status: string | null
    current_step: string | null
    copy: string
    active_session_count: number
    last_session_update_at: string | null
  }
  activity: {
    last_llm_call_at: string | null
    last_step_timing_at: string | null
    last_activity_at: string | null
  }
  spend: {
    total_usd: number
    by_flow: { flow_type: string; cost_usd: number; calls: number; last_call_at: string | null }[]
  }
  health: {
    failed_step_count: number
    last_failed_step_at: string | null
    source: string
    confidence: string
    note: string
  }
  classifications: Record<string, string>
}

interface UserJourneyResponse {
  data: UserJourney
}


// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function MonthSelector({
  month, year, onChange,
}: {
  month: number
  year: number
  onChange: (month: number, year: number) => void
}) {
  const prev = () => {
    const m = month === 1 ? 12 : month - 1
    const y = month === 1 ? year - 1 : year
    onChange(m, y)
  }
  const next = () => {
    const m = month === 12 ? 1 : month + 1
    const y = month === 12 ? year + 1 : year
    onChange(m, y)
  }
  return (
    <div className="flex items-center gap-3 text-secondary text-sm font-medium">
      <button onClick={prev} className="hover:text-primary transition-colors">
        <span className="material-symbols-outlined text-lg">chevron_left</span>
      </button>
      <span className="font-['IBM_Plex_Serif'] italic text-base">
        {MONTH_NAMES[month - 1]} {year}
      </span>
      <button onClick={next} className="hover:text-primary transition-colors">
        <span className="material-symbols-outlined text-lg">chevron_right</span>
      </button>
    </div>
  )
}

const thCls = 'px-6 py-4 text-[11px] font-semibold text-secondary uppercase tracking-wider text-left'
const tdCls = 'px-6 py-5'

// ---------------------------------------------------------------------------
// User Journey drawer (read-only diagnostic — see docs/ADMIN_USER_JOURNEY_SPEC.md)
// ---------------------------------------------------------------------------

function ClassTag({ kind }: { kind: FactClass | string }) {
  const map: Record<string, string> = {
    authoritative: 'bg-green-50 text-green-700 border-green-100',
    best_effort: 'bg-amber-50 text-amber-700 border-amber-100',
    potentially_stale: 'bg-stone-100 text-stone-600 border-stone-200',
  }
  const label = kind.replace('_', '-')
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border align-middle ${map[kind] ?? map.potentially_stale}`}
    >
      {label}
    </span>
  )
}

function JourneySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="text-[10px] font-bold text-secondary uppercase tracking-widest">{title}</h4>
      <div className="space-y-1.5 text-sm text-on-surface">{children}</div>
    </div>
  )
}

function JourneyRow({
  label,
  value,
  cls,
}: {
  label: string
  value: React.ReactNode
  cls?: FactClass | string
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-secondary">{label}</span>
      <span className="text-right text-on-surface">
        {value} {cls && <ClassTag kind={cls} />}
      </span>
    </div>
  )
}

/** Timestamp rendered as relative time, with the absolute time as a tooltip. */
function TimeValue({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-secondary">—</span>
  return <span title={absoluteTime(iso)}>{relativeTime(iso)}</span>
}

// Condensed interpretation guide from spec §4.1 — static reference copy.
const INTERPRETATION_GUIDE: { state: string; signal: string; meaning: string }[] = [
  { state: 'awaiting_user @ interrogation', signal: 'active minutes ago', meaning: 'Normal — actively answering.' },
  { state: 'awaiting_user @ interrogation', signal: 'idle hours/days', meaning: 'Idle: waiting on the user (no backend fault implied).' },
  { state: 'in_progress @ analysis/assembly', signal: 'idle beyond expected', meaning: 'Suspect stall/crash — task likely died; check for a recorded error.' },
  { state: 'awaiting_user @ review/editing', signal: 'idle hours/days', meaning: 'Reviewing/editing without LLM calls, or abandoned.' },
  { state: 'onboarding @ review', signal: 'idle hours/days', meaning: 'Stalled at Library review — common drop-off.' },
  { state: 'any', signal: 'a recorded failed step', meaning: 'A step errored for this user — strongest "something broke" signal.' },
]

function UserJourneyDrawer({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const [journey, setJourney] = useState<UserJourney | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showGuide, setShowGuide] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<UserJourneyResponse>(`/api/v1/admin/users/${user.id}/journey`)
      setJourney(res.data)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Failed to load journey')
    } finally {
      setLoading(false)
    }
  }, [user.id])

  useEffect(() => { load() }, [load])

  const c = journey?.classifications ?? {}

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-md h-screen overflow-y-auto bg-surface-container-lowest shadow-2xl p-6 space-y-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-headline text-lg text-on-surface">User Journey</h3>
            <p className="text-xs text-secondary break-all">{user.email}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={load}
              disabled={loading}
              className="text-xs text-secondary hover:text-primary-container transition-colors disabled:opacity-40"
            >
              Refresh
            </button>
            <button onClick={onClose} className="text-secondary hover:text-on-surface">
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>
        </div>

        {loading && <LoadingSpinner />}
        {error && <ErrorMsg>{error}</ErrorMsg>}

        {journey && !loading && (
          <div className="space-y-6">
            <div className="rounded-xl bg-surface-container-low p-4 text-sm text-on-surface leading-relaxed">
              {journey.summary}
            </div>

            <JourneySection title="Account">
              <JourneyRow label="Status" value={journey.user.account_status} />
              <JourneyRow
                label="Key mode"
                value={journey.user.api_key_mode === 'byo' ? 'BYO' : 'Managed'}
              />
              <JourneyRow label="Joined" value={<TimeValue iso={journey.user.joined_at} />} />
              <JourneyRow
                label="Last login"
                value={<TimeValue iso={journey.user.last_login_at} />}
                cls={c['user.last_login_at']}
              />
            </JourneySection>

            <JourneySection title="Onboarding">
              <JourneyRow
                label="State"
                value={journey.onboarding.completed ? 'Completed' : (journey.onboarding.current_step ?? 'Not started')}
                cls={c['onboarding.completed']}
              />
              {journey.onboarding.completed && (
                <JourneyRow
                  label="When"
                  value={<TimeValue iso={journey.onboarding.completed_at} />}
                  cls={c['onboarding.completed_at']}
                />
              )}
              <p className="text-xs text-secondary italic">{journey.onboarding.copy}</p>
            </JourneySection>

            <JourneySection title="Library">
              <JourneyRow
                label="Proofs"
                value={`${journey.library.active} active · ${journey.library.needs_review} needs review · ${journey.library.archived} archived · ${journey.library.discarded} discarded`}
                cls={c['library.active']}
              />
            </JourneySection>

            <JourneySection title="CV generation">
              {journey.cv_generation.exists ? (
                <>
                  <p className="text-[10px] font-semibold text-secondary uppercase tracking-wider">
                    Current state (what the system believes)
                  </p>
                  <JourneyRow
                    label="status"
                    value={journey.cv_generation.status ?? '—'}
                    cls={c['cv_generation.status']}
                  />
                  <JourneyRow label="step" value={journey.cv_generation.current_step ?? '—'} />
                  <p className="text-xs text-secondary italic">{journey.cv_generation.copy}</p>
                  <p className="text-[10px] font-semibold text-secondary uppercase tracking-wider pt-2">
                    Activity (what actually happened)
                  </p>
                  <JourneyRow
                    label="Last session update"
                    value={<TimeValue iso={journey.cv_generation.last_session_update_at} />}
                  />
                  {journey.cv_generation.active_session_count > 1 && (
                    <p className="text-xs text-amber-700">
                      ⚠ {journey.cv_generation.active_session_count} active CV sessions — the
                      single-active invariant is not enforced in code.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-secondary">Has not started a CV.</p>
              )}
            </JourneySection>

            <JourneySection title="Activity">
              <JourneyRow
                label="Last LLM call"
                value={<TimeValue iso={journey.activity.last_llm_call_at} />}
                cls={c['activity.last_llm_call_at']}
              />
              <JourneyRow
                label="Last step timing"
                value={<TimeValue iso={journey.activity.last_step_timing_at} />}
                cls={c['activity.last_step_timing_at']}
              />
            </JourneySection>

            <JourneySection title="Spend">
              <JourneyRow
                label="Total"
                value={`$${journey.spend.total_usd.toFixed(2)}`}
                cls={c['spend.total_usd']}
              />
              {journey.spend.by_flow.map((f) => (
                <JourneyRow
                  key={f.flow_type}
                  label={f.flow_type}
                  value={
                    <span>
                      ${f.cost_usd.toFixed(2)} · {f.calls} calls ·{' '}
                      <TimeValue iso={f.last_call_at} />
                    </span>
                  }
                />
              ))}
              {journey.spend.by_flow.length > 0 && <div className="pt-0.5"><ClassTag kind={c['spend.by_flow'] ?? 'best_effort'} /></div>}
            </JourneySection>

            <JourneySection title="Health">
              {journey.health.failed_step_count > 0 ? (
                <JourneyRow
                  label="Step errors"
                  value={
                    <span>
                      {journey.health.failed_step_count} · most recent{' '}
                      <TimeValue iso={journey.health.last_failed_step_at} />
                    </span>
                  }
                  cls={c['health.failed_step_count']}
                />
              ) : (
                <JourneyRow
                  label="Step errors"
                  value="None recorded"
                  cls={c['health.failed_step_count']}
                />
              )}
              <p className="text-xs text-secondary italic">{journey.health.note}</p>
            </JourneySection>

            <div>
              <button
                onClick={() => setShowGuide((s) => !s)}
                className="text-xs text-primary-container hover:underline"
              >
                {showGuide ? 'Hide' : 'Show'} interpretation guide
              </button>
              {showGuide && (
                <div className="mt-3 space-y-2 rounded-lg bg-surface-container-low p-3">
                  {INTERPRETATION_GUIDE.map((g, i) => (
                    <div key={i} className="text-xs">
                      <span className="font-medium text-on-surface">{g.state}</span>
                      <span className="text-secondary"> + {g.signal}</span>
                      <span className="text-secondary"> → {g.meaning}</span>
                    </div>
                  ))}
                  <p className="text-[11px] text-secondary italic pt-1">
                    No timeout sweeper exists for onboarding or CV generation — read staleness as
                    one of these, never a definitive "abandoned."
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------

function UsersTab() {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [data, setData] = useState<AdminUsersResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [quotaPopover, setQuotaPopover] = useState<string | null>(null)
  const [quotaInput, setQuotaInput] = useState('')
  const [savingQuota, setSavingQuota] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState<string | null>(null)
  const [confirmSuspend, setConfirmSuspend] = useState<AdminUser | null>(null)
  const [journeyUser, setJourneyUser] = useState<AdminUser | null>(null)
  const [uiFilter, setUiFilter] = useState<UiVersionFilterValue>('all')
  const popoverRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async (m: number, y: number) => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<AdminUsersResponse>(`/api/v1/admin/users?month=${m}&year=${y}`)
      setData(res)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(month, year) }, [month, year, load])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setQuotaPopover(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleOpenQuota = (userId: string, cap: number | null) => {
    setQuotaPopover(userId)
    setQuotaInput(cap != null ? String(cap) : '10')
  }

  const handleSaveQuota = async (userId: string) => {
    setSavingQuota(true)
    try {
      await api.put(`/api/v1/admin/users/${userId}/quota`, {
        monthly_cap_usd: parseFloat(quotaInput),
      })
      setQuotaPopover(null)
      load(month, year)
    } catch { /* no-op */ } finally {
      setSavingQuota(false)
    }
  }

  const handleToggleStatus = async (user: AdminUser) => {
    if (user.account_status === 'active') {
      // Suspend requires confirmation — open modal instead of acting immediately
      setConfirmSuspend(user)
      return
    }
    // Unsuspend is safe — no confirmation needed
    setTogglingStatus(user.id)
    try {
      await api.put(`/api/v1/admin/users/${user.id}/status`, { status: 'active' })
      load(month, year)
    } catch { /* no-op */ } finally {
      setTogglingStatus(null)
    }
  }

  const handleConfirmSuspend = async () => {
    if (!confirmSuspend) return
    setTogglingStatus(confirmSuspend.id)
    setConfirmSuspend(null)
    try {
      await api.put(`/api/v1/admin/users/${confirmSuspend.id}/status`, { status: 'suspended' })
      load(month, year)
    } catch { /* no-op */ } finally {
      setTogglingStatus(null)
    }
  }

  const handleUiVersionChanged = (userId: string, version: UiVersion) =>
    setData((d) =>
      d && { ...d, users: d.users.map((u) => (u.id === userId ? { ...u, ui_version: version } : u)) },
    )

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!data) return null

  // The filter only narrows the rows; the totals above still count everyone.
  const shownUsers = data.users.filter(
    (u) => uiFilter === 'all' || toUiVersion(u.ui_version) === uiFilter,
  )

  const totalSpend = data.total_spend_this_month ?? 0
  const avgSpend = data.active_user_count > 0 ? totalSpend / data.active_user_count : 0

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-3 gap-6">
        <SummaryCard label="Total spend this month" value={`$${totalSpend.toFixed(2)}`} />
        <SummaryCard label="Active users this month" value={String(data.active_user_count)} />
        <SummaryCard label="Avg spend per user" value={`$${avgSpend.toFixed(2)}`} />
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <UiVersionFilter value={uiFilter} onChange={setUiFilter} />
          <MonthSelector
            month={month}
            year={year}
            onChange={(m, y) => { setMonth(m); setYear(y) }}
          />
        </div>

        <div className="overflow-hidden border border-outline-variant/30 rounded-xl bg-surface-container-lowest">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-container-low border-b border-outline-variant/30">
                <th className={thCls}>Name &amp; Email</th>
                <th className={thCls}>Status</th>
                <th className={thCls}>Key mode</th>
                <th className={thCls}>UI</th>
                <th className={thCls}>CVs this month</th>
                <th className={thCls}>Spend this month</th>
                <th className={thCls}>All-time spend</th>
                <th className={`${thCls} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {shownUsers.map((u) => (
                <tr key={u.id} className="hover:bg-surface-container/30 transition-colors">
                  <td className={tdCls}>
                    <div className="font-medium text-on-surface">{u.display_name ?? u.email}</div>
                    <div className="text-xs text-secondary">{u.email}</div>
                  </td>
                  <td className={tdCls}><StatusBadge status={u.account_status} /></td>
                  <td className={`${tdCls} text-sm text-secondary`}>
                    {u.api_key_mode === 'byo' ? 'BYO' : 'Managed'}
                  </td>
                  <td className={tdCls}>
                    <UiVersionCell
                      userId={u.id}
                      version={toUiVersion(u.ui_version)}
                      onChanged={handleUiVersionChanged}
                    />
                  </td>
                  <td className={`${tdCls} text-sm text-on-surface`}>{u.cv_count_this_month}</td>
                  <td className={`${tdCls} text-sm font-medium text-on-surface`}>
                    ${u.spend_this_month_usd.toFixed(2)}
                  </td>
                  <td className={`${tdCls} text-sm text-secondary`}>
                    ${u.spend_all_time_usd.toFixed(2)}
                  </td>
                  <td className={`${tdCls} text-right`}>
                    <div className="flex items-center justify-end gap-3 relative">
                      <button
                        onClick={() => setJourneyUser(u)}
                        className="text-xs text-secondary hover:text-primary-container transition-colors"
                      >
                        Journey
                      </button>

                      {!u.is_admin && (
                        <button
                          onClick={() => handleToggleStatus(u)}
                          disabled={togglingStatus === u.id}
                          className={
                            u.account_status === 'active'
                              ? 'text-xs text-secondary hover:text-error transition-colors disabled:opacity-40'
                              : 'text-xs text-primary-container hover:underline transition-colors font-medium disabled:opacity-40'
                          }
                        >
                          {togglingStatus === u.id ? '…' : u.account_status === 'active' ? 'Suspend' : 'Unsuspend'}
                        </button>
                      )}

                      {u.api_key_mode !== 'byo' && (
                        <div className="relative" ref={quotaPopover === u.id ? popoverRef : null}>
                          <button
                            onClick={() => handleOpenQuota(u.id, u.monthly_cap_usd)}
                            className="text-xs text-secondary hover:text-primary-container transition-colors"
                          >
                            Edit quota
                          </button>
                          {quotaPopover === u.id && (
                            <div className="absolute right-0 mt-2 w-48 p-4 bg-surface-container-lowest border border-outline-variant/30 shadow-[0_4px_20px_rgba(26,28,26,0.08)] rounded-xl z-20">
                              <label className="block text-[10px] font-bold text-secondary uppercase tracking-widest mb-2">
                                Monthly cap ($)
                              </label>
                              <input
                                type="number"
                                value={quotaInput}
                                onChange={(e) => setQuotaInput(e.target.value)}
                                className="w-full bg-surface-container-low border-none text-sm px-3 py-2 rounded-lg focus:ring-1 focus:ring-primary-container outline-none mb-3"
                              />
                              <button
                                onClick={() => handleSaveQuota(u.id)}
                                disabled={savingQuota}
                                className="w-full bg-primary-container text-on-primary text-xs py-2 rounded-lg font-medium hover:bg-primary transition-colors disabled:opacity-50"
                              >
                                {savingQuota ? '…' : 'Save'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suspend confirmation modal */}
      {confirmSuspend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-2xl p-6 space-y-4">
            <h3 className="font-semibold text-stone-800">Suspend account?</h3>
            <p className="text-sm text-stone-600">
              <span className="font-medium">{confirmSuspend.display_name ?? confirmSuspend.email}</span> will
              be immediately locked out. You can unsuspend them at any time.
            </p>
            <div className="flex justify-end gap-3 pt-1">
              <button
                onClick={() => setConfirmSuspend(null)}
                className="rounded px-4 py-2 text-sm text-stone-500 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSuspend}
                className="rounded px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700"
              >
                Suspend
              </button>
            </div>
          </div>
        </div>
      )}

      {journeyUser && (
        <UserJourneyDrawer user={journeyUser} onClose={() => setJourneyUser(null)} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Invites tab
// ---------------------------------------------------------------------------

function InvitesTab() {
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({
    label: '',
    api_key_mode: 'managed',
    monthly_cap_usd: '10',
    max_uses: '1',
  })
  const [creating, setCreating] = useState(false)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [renewing, setRenewing] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ invites: Invite[] }>('/api/v1/admin/invites')
      setInvites(res.invites)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Failed to load invites')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      await api.post('/api/v1/admin/invites', {
        label: form.label.trim(),
        api_key_mode: form.api_key_mode,
        monthly_cap_usd: form.api_key_mode === 'managed' ? parseFloat(form.monthly_cap_usd) : null,
        max_uses: parseInt(form.max_uses, 10),
      })
      setShowModal(false)
      setForm({ label: '', api_key_mode: 'managed', monthly_cap_usd: '10', max_uses: '1' })
      load()
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Failed to create invite')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (id: string) => {
    setRevoking(id)
    try {
      await api.post(`/api/v1/admin/invites/${id}/revoke`)
      load()
    } catch { /* no-op */ } finally { setRevoking(null) }
  }

  const handleRenew = async (id: string) => {
    setRenewing(id)
    try {
      const updated = await api.post<Invite>(`/api/v1/admin/invites/${id}/renew`)
      setInvites((prev) => prev.map((inv) => (inv.id === id ? updated : inv)))
    } catch { /* no-op */ } finally { setRenewing(null) }
  }

  const copyLink = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(id)
      setTimeout(() => setCopied(null), 2000)
    } catch { /* no-op */ }
  }

  const shareLink = (url: string, label: string) => {
    if (navigator.share) {
      navigator.share({ title: `Proof of Work invite for ${label}`, url }).catch(() => {})
    }
  }

  const statusColor = (s: string) => {
    if (s === 'pending') return 'bg-amber-50 text-amber-700 border-amber-100'
    if (s === 'accepted' || s === 'exhausted') return 'bg-green-50 text-green-700 border-emerald-100'
    if (s === 'expired') return 'bg-orange-50 text-orange-700 border-orange-100'
    return 'bg-surface-container text-secondary border-outline-variant/30'
  }

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      {error && <ErrorMsg>{error}</ErrorMsg>}

      <div className="flex justify-end">
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-primary-container text-on-primary rounded-xl text-sm font-semibold hover:bg-primary transition-all"
        >
          <span className="material-symbols-outlined text-lg">add</span>
          New Invite
        </button>
      </div>

      {invites.length === 0 ? (
        <div className="text-center py-16 text-secondary text-sm">No invites yet.</div>
      ) : (
        <div className="overflow-hidden border border-outline-variant/30 rounded-xl bg-surface-container-lowest">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-container-low border-b border-outline-variant/30">
                <th className={thCls}>Name</th>
                <th className={thCls}>Key mode</th>
                <th className={thCls}>Uses</th>
                <th className={thCls}>Status</th>
                <th className={thCls}>Created</th>
                <th className={thCls}>Expires</th>
                <th className={`${thCls} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/20">
              {invites.map((inv) => (
                <tr key={inv.id} className="hover:bg-surface-container/30 transition-colors">
                  <td className={`${tdCls} font-medium text-on-surface`}>{inv.label}</td>
                  <td className={`${tdCls} text-sm text-secondary`}>
                    {inv.api_key_mode_granted === 'byo' ? 'BYO' : 'Managed'}
                  </td>
                  <td className={`${tdCls} text-sm text-on-surface`}>
                    {inv.use_count}/{inv.max_uses}
                  </td>
                  <td className={tdCls}>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border uppercase tracking-wide ${statusColor(inv.status)}`}
                    >
                      {inv.status}
                    </span>
                  </td>
                  <td className={`${tdCls} text-sm text-secondary`}>{fmt(inv.created_at)}</td>
                  <td className={`${tdCls} text-sm text-secondary`}>{fmt(inv.expires_at)}</td>
                  <td className={`${tdCls} text-right`}>
                    <div className="flex items-center justify-end gap-3">
                      {inv.status === 'pending' && inv.invite_url && (
                        <>
                          <button
                            onClick={() => copyLink(inv.invite_url ?? '', inv.id)}
                            title="Copy link"
                            className="text-secondary hover:text-on-surface transition-colors"
                          >
                            <span className="material-symbols-outlined text-[18px]">
                              {copied === inv.id ? 'check' : 'content_copy'}
                            </span>
                          </button>
                          {typeof navigator !== 'undefined' && 'share' in navigator && (
                            <button
                              onClick={() => shareLink(inv.invite_url ?? '', inv.label)}
                              title="Share"
                              className="text-secondary hover:text-on-surface transition-colors"
                            >
                              <span className="material-symbols-outlined text-[18px]">ios_share</span>
                            </button>
                          )}
                          <button
                            onClick={() => handleRevoke(inv.id)}
                            disabled={revoking === inv.id}
                            title="Revoke"
                            className="text-secondary hover:text-error transition-colors disabled:opacity-40"
                          >
                            <span className="material-symbols-outlined text-[18px]">cancel</span>
                          </button>
                        </>
                      )}
                      {inv.status === 'expired' && (
                        <button
                          onClick={() => handleRenew(inv.id)}
                          disabled={renewing === inv.id}
                          className="text-xs font-bold text-primary-container hover:underline transition-colors disabled:opacity-40"
                        >
                          {renewing === inv.id ? '…' : 'Renew'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/20 backdrop-blur-sm">
          <div className="w-full max-w-[440px] bg-surface-container-lowest rounded-xl shadow-[0_20px_40px_rgba(26,28,26,0.12)] overflow-hidden">
            <div className="px-8 py-6 flex items-center justify-between border-b border-outline-variant/20">
              <h3 className="text-xl font-headline text-on-surface">New Invite</h3>
              <button
                onClick={() => setShowModal(false)}
                className="material-symbols-outlined text-secondary hover:text-on-surface transition-colors"
              >
                close
              </button>
            </div>
            <div className="px-8 py-6 space-y-5">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-secondary">
                  Name
                </label>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Alice"
                  className="w-full px-4 py-3 bg-surface-bright border border-outline-variant/40 rounded-xl focus:ring-1 focus:ring-primary-container outline-none text-on-surface"
                />
                {form.label.trim() && (
                  <p className="text-xs text-secondary">
                    Invitee will see: <span className="font-medium text-on-surface">You've been invited, {form.label.trim()}</span>
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-secondary">
                  API key mode
                </label>
                <div className="flex gap-6">
                  {(['managed', 'byo'] as const).map((m) => (
                    <label key={m} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="invite_api_key_mode"
                        value={m}
                        checked={form.api_key_mode === m}
                        onChange={() => setForm({ ...form, api_key_mode: m })}
                        className="accent-primary-container"
                      />
                      <span className="text-sm text-on-surface">
                        {m === 'managed' ? 'Managed key' : 'BYO key'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {form.api_key_mode === 'managed' && (
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-secondary">
                    Monthly usage cap ($)
                  </label>
                  <input
                    type="number"
                    value={form.monthly_cap_usd}
                    onChange={(e) => setForm({ ...form, monthly_cap_usd: e.target.value })}
                    className="w-full px-4 py-3 bg-surface-bright border border-outline-variant/40 rounded-xl focus:ring-1 focus:ring-primary-container outline-none text-on-surface"
                  />
                  <p className="text-xs text-secondary">Maximum LLM spend per month for this user.</p>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-secondary">
                  Max uses
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  className="w-full px-4 py-3 bg-surface-bright border border-outline-variant/40 rounded-xl focus:ring-1 focus:ring-primary-container outline-none text-on-surface"
                />
                <p className="text-xs text-secondary">
                  {form.max_uses === '1'
                    ? 'Single-use — link works once then expires.'
                    : `Multi-use — up to ${form.max_uses} people can use this link.`}
                </p>
              </div>
            </div>
            <div className="px-8 py-5 bg-surface-container-low">
              <button
                onClick={handleCreate}
                disabled={!form.label.trim() || creating}
                className="w-full py-3 bg-primary-container text-on-primary rounded-xl text-sm font-semibold hover:bg-primary transition-all disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Effectiveness tab
// ---------------------------------------------------------------------------

function EffectivenessTab() {
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const [data, setData] = useState<EffectivenessResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (m: number, y: number) => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<EffectivenessResponse>(
        `/api/v1/admin/dashboard/effectiveness?month=${m}&year=${y}`,
      )
      setData(res)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.error.message : 'Failed to load effectiveness data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load(month, year) }, [month, year, load])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMsg>{error}</ErrorMsg>
  if (!data) return null

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-3 gap-6">
        <SummaryCard label="Avg iterations per CV" value={String(data.avg_iterations_overall)} sub="Edit → Review cycles" />
        <SummaryCard label="Avg questions asked" value={String(data.avg_questions_overall)} sub="Per generation session" />
        <SummaryCard label="CVs approved this month" value={String(data.cvs_approved_this_month)} />
      </div>

      <div className="flex justify-end">
        <MonthSelector month={month} year={year} onChange={(m, y) => { setMonth(m); setYear(y) }} />
      </div>

      <div className="overflow-hidden border border-outline-variant/30 rounded-xl bg-surface-container-lowest">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-surface-container-low border-b border-outline-variant/30">
              <th className={thCls}>User</th>
              <th className={thCls}>CVs generated</th>
              <th className={thCls}>Avg iterations</th>
              <th className={thCls}>Avg questions</th>
              <th className={thCls}>Last active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/20">
            {data.entries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-secondary text-sm">
                  No data for this period.
                </td>
              </tr>
            ) : (
              data.entries.map((e) => (
                <tr key={e.user_id} className="hover:bg-surface-container/30 transition-colors">
                  <td className={`${tdCls} text-sm font-medium text-on-surface`}>{e.email}</td>
                  <td className={`${tdCls} text-sm text-on-surface`}>{e.cv_count}</td>
                  <td className={`${tdCls} text-sm text-on-surface`}>{e.avg_iterations}</td>
                  <td className={`${tdCls} text-sm text-on-surface`}>{e.avg_questions}</td>
                  <td className={`${tdCls} text-sm text-secondary`}>
                    {e.last_active
                      ? new Date(e.last_active).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Vocabulary tab
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Vocabulary sub-components
// ---------------------------------------------------------------------------

function TypeBadge({ type }: { type: string }) {
  const cls =
    type === 'competency'
      ? 'bg-primary/10 text-primary border border-primary/20'
      : 'bg-tertiary/10 text-tertiary border border-tertiary/20'
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {type === 'competency' ? 'Capability' : 'Context'}
    </span>
  )
}

function InlineEditField({
  value,
  placeholder,
  onSave,
}: {
  value: string
  placeholder: string
  onSave: (v: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (draft.trim() === value) { setEditing(false); return }
    setSaving(true)
    await onSave(draft.trim())
    setSaving(false)
    setEditing(false)
  }

  if (!editing) {
    return (
      <button
        onClick={() => { setDraft(value); setEditing(true) }}
        className="text-left text-sm text-secondary hover:text-on-surface transition-colors"
      >
        {value || <span className="italic text-secondary/50">{placeholder}</span>}
      </button>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        className="text-sm bg-surface-container border border-outline-variant/40 rounded px-2 py-1 text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/40 w-48"
      />
      <button onClick={save} disabled={saving} className="text-[11px] font-semibold text-primary disabled:opacity-40">
        {saving ? '…' : 'Save'}
      </button>
      <button onClick={() => setEditing(false)} className="text-[11px] text-secondary">Cancel</button>
    </div>
  )
}

function MergeTypeahead({
  vocabularyType,
  terms,
  onMerge,
  onCancel,
}: {
  vocabularyType: string
  terms: VocabTerm[]
  onMerge: (targetId: string) => Promise<void>
  onCancel: () => void
}) {
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState<VocabTerm | null>(null)

  const filtered = terms
    .filter((t) => t.vocabulary_type === vocabularyType && t.term.includes(query.toLowerCase()))
    .slice(0, 8)

  const confirm = async () => {
    if (!selected) return
    setSaving(true)
    await onMerge(selected.id)
    setSaving(false)
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative">
        <input
          autoFocus
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null) }}
          placeholder="Search term…"
          className="text-sm bg-surface-container border border-outline-variant/40 rounded px-2 py-1 text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/40 w-40"
        />
        {query && !selected && filtered.length > 0 && (
          <div className="absolute top-full left-0 mt-1 z-10 bg-surface-container-highest border border-outline-variant/40 rounded-lg shadow-lg min-w-[12rem] max-h-48 overflow-y-auto">
            {filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => { setSelected(t); setQuery(t.term) }}
                className="block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-container-low transition-colors"
              >
                {t.term}
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={confirm}
        disabled={!selected || saving}
        className="text-[11px] font-semibold text-primary disabled:opacity-40"
      >
        {saving ? '…' : 'Confirm merge'}
      </button>
      <button onClick={onCancel} className="text-[11px] text-secondary">Cancel</button>
    </div>
  )
}

function CandidateRow({ candidate, terms }: { candidate: VocabCandidate; terms: VocabTerm[] }) {
  const { approveCandidiate, rejectCandidate, mergeCandidate, acting } = useVocabularyStore()
  const [merging, setMerging] = useState(false)
  const busy = acting === candidate.id

  return (
    <tr className="hover:bg-surface-container/30 transition-colors">
      <td className={tdCls}>
        <div className="font-medium text-sm text-on-surface">{candidate.proposed_term}</div>
        {candidate.normalized_term && candidate.normalized_term !== candidate.proposed_term && (
          <div className="text-[10px] text-secondary/60 mt-0.5">→ {candidate.normalized_term}</div>
        )}
        {candidate.raw_forms.length > 1 && (
          <div className="text-[10px] text-secondary/60 mt-0.5" title={candidate.raw_forms.join(', ')}>
            +{candidate.raw_forms.length - 1} form{candidate.raw_forms.length > 2 ? 's' : ''}
          </div>
        )}
      </td>
      <td className={tdCls}><TypeBadge type={candidate.vocabulary_type} /></td>
      <td className={`${tdCls} text-sm text-on-surface tabular-nums`}>{candidate.occurrence_count}</td>
      <td className={`${tdCls} text-sm text-secondary`}>
        {candidate.nearest_existing_term ?? '—'}
        {candidate.similarity_score != null && (
          <span className="ml-1 text-[10px] text-secondary/50">
            ({Math.round(candidate.similarity_score * 100)}%)
          </span>
        )}
      </td>
      <td className={`${tdCls} text-sm text-secondary`}>
        {new Date(candidate.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
      </td>
      <td className={`${tdCls} text-right`}>
        {merging ? (
          <MergeTypeahead
            vocabularyType={candidate.vocabulary_type}
            terms={terms}
            onMerge={async (targetId) => { await mergeCandidate(candidate.id, targetId); setMerging(false) }}
            onCancel={() => setMerging(false)}
          />
        ) : (
          <div className="flex items-center justify-end gap-4">
            <button
              onClick={() => approveCandidiate(candidate.id)}
              disabled={busy}
              className="text-xs font-bold text-primary-container hover:underline transition-colors disabled:opacity-40"
            >
              {busy ? '…' : 'Approve'}
            </button>
            <button
              onClick={() => setMerging(true)}
              disabled={busy}
              className="text-xs font-bold text-secondary hover:text-on-surface transition-colors disabled:opacity-40"
            >
              Merge into
            </button>
            <button
              onClick={() => rejectCandidate(candidate.id)}
              disabled={busy}
              className="text-xs font-bold text-secondary hover:text-error transition-colors disabled:opacity-40"
            >
              Reject
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

function TermRow({ term, terms }: { term: VocabTerm; terms: VocabTerm[] }) {
  const { patchTerm, addAlias, retireTerm, mergeTerms, acting } = useVocabularyStore()
  const [addingAlias, setAddingAlias] = useState(false)
  const [aliasDraft, setAliasDraft] = useState('')
  const [mergingWith, setMergingWith] = useState(false)
  const [confirmingRetire, setConfirmingRetire] = useState(false)
  const busy = acting === term.id

  const saveAlias = async () => {
    if (!aliasDraft.trim()) return
    await addAlias(term.id, aliasDraft.trim())
    setAliasDraft('')
    setAddingAlias(false)
  }

  return (
    <tr className="hover:bg-surface-container/30 transition-colors">
      <td className={tdCls}>
        <div className="font-medium text-sm text-on-surface">{term.term}</div>
        {term.aliases.length > 0 && (
          <div className="text-[10px] text-secondary/60 mt-0.5" title={term.aliases.join(', ')}>
            {term.aliases.length} alias{term.aliases.length > 1 ? 'es' : ''}
          </div>
        )}
      </td>
      <td className={tdCls}><TypeBadge type={term.vocabulary_type} /></td>
      <td className={`${tdCls} text-secondary/50 tabular-nums`}>—</td>
      <td className={`${tdCls} text-sm text-secondary`}>
        <InlineEditField
          value={term.definition ?? ''}
          placeholder="Add definition…"
          onSave={(v) => patchTerm(term.id, { definition: v })}
        />
      </td>
      <td className={`${tdCls} text-sm text-secondary`}>
        {new Date(term.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
      </td>
      <td className={`${tdCls} text-right`}>
        {mergingWith ? (
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-[11px] text-secondary">
              Retire <span className="font-semibold text-on-surface">{term.term}</span> and fold it into →
            </span>
            <MergeTypeahead
              vocabularyType={term.vocabulary_type}
              terms={terms.filter((t) => t.id !== term.id)}
              onMerge={async (intoId) => { await mergeTerms(term.id, intoId); setMergingWith(false) }}
              onCancel={() => setMergingWith(false)}
            />
          </div>
        ) : addingAlias ? (
          <div className="flex items-center gap-2 justify-end">
            <input
              autoFocus
              value={aliasDraft}
              onChange={(e) => setAliasDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveAlias(); if (e.key === 'Escape') setAddingAlias(false) }}
              placeholder="alias"
              className="text-sm bg-surface-container border border-outline-variant/40 rounded px-2 py-1 text-on-surface focus:outline-none w-32"
            />
            <button onClick={saveAlias} disabled={busy} className="text-[11px] font-semibold text-primary disabled:opacity-40">
              {busy ? '…' : 'Save'}
            </button>
            <button onClick={() => setAddingAlias(false)} className="text-[11px] text-secondary">Cancel</button>
          </div>
        ) : confirmingRetire ? (
          <div className="flex flex-col items-end gap-1.5">
            <span className="text-[11px] text-secondary">
              Remove <span className="font-semibold text-on-surface">{term.term}</span> from the active vocabulary?
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => { await retireTerm(term.id); setConfirmingRetire(false) }}
                disabled={busy}
                className="text-[11px] font-bold text-error disabled:opacity-40"
              >
                {busy ? '…' : 'Confirm retire'}
              </button>
              <button onClick={() => setConfirmingRetire(false)} className="text-[11px] text-secondary">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-4">
            <button
              onClick={() => setAddingAlias(true)}
              disabled={busy}
              className="text-xs font-bold text-secondary hover:text-on-surface transition-colors disabled:opacity-40"
            >
              + Alias
            </button>
            <button
              onClick={() => setMergingWith(true)}
              disabled={busy}
              className="text-xs font-bold text-secondary hover:text-on-surface transition-colors disabled:opacity-40"
            >
              Merge with
            </button>
            <button
              onClick={() => setConfirmingRetire(true)}
              disabled={busy}
              className="text-xs font-bold text-secondary hover:text-error transition-colors disabled:opacity-40"
            >
              Retire
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

function AutoLinkedPanel() {
  const { autoLinked, autoLinkedLoading, loadAutoLinked, flagAutoLinked, acting } = useVocabularyStore()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (open && autoLinked.length === 0) loadAutoLinked()
  }, [open, autoLinked.length, loadAutoLinked])

  return (
    <div className="border border-outline-variant/30 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface-container-low text-sm font-semibold text-on-surface hover:bg-surface-container transition-colors"
      >
        <span>Auto-linked log (spot-check)</span>
        <span className="text-secondary text-xs">{open ? '▲ Hide' : '▼ Show'}</span>
      </button>
      {open && (
        <div className="p-4">
          {autoLinkedLoading ? (
            <LoadingSpinner />
          ) : autoLinked.length === 0 ? (
            <p className="text-sm text-secondary">No auto-linked entries yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-outline-variant/20">
                    <th className={thCls}>Proposed</th>
                    <th className={thCls}>Normalised → linked to</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Match</th>
                    <th className={thCls}>First seen</th>
                    <th className={`${thCls} text-right`}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/20">
                  {autoLinked.map((c) => (
                    <tr key={c.id} className="hover:bg-surface-container/20">
                      <td className={tdCls}>{c.proposed_term}</td>
                      <td className={`${tdCls} text-secondary`}>{c.normalized_term ?? '—'}</td>
                      <td className={tdCls}><TypeBadge type={c.vocabulary_type} /></td>
                      <td className={`${tdCls} text-secondary text-xs`}>
                        {c.similarity_score != null
                          ? `fuzzy ${Math.round(c.similarity_score * 100)}%`
                          : c.nearest_existing_term
                            ? 'alias'
                            : 'exact'}
                      </td>
                      <td className={`${tdCls} text-secondary`}>
                        {new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className={`${tdCls} text-right`}>
                        <button
                          onClick={() => flagAutoLinked(c.id)}
                          disabled={acting === c.id}
                          className="text-xs font-bold text-secondary hover:text-error transition-colors disabled:opacity-40"
                        >
                          {acting === c.id ? '…' : 'Flag for review'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function VocabularyTab({ onPendingCount }: { onPendingCount: (n: number) => void }) {
  const {
    terms,
    candidates,
    filter,
    typeFilter,
    loading,
    error,
    load,
    setFilter,
    setTypeFilter,
    pendingCount,
  } = useVocabularyStore()

  useEffect(() => { load() }, [load])
  useEffect(() => { onPendingCount(pendingCount()) }, [candidates, onPendingCount, pendingCount])

  const pending = candidates.filter((c) => c.status === 'pending')

  const handleTypeFilter = (t: string) => {
    setTypeFilter(t)
    load()
  }

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMsg>{error}</ErrorMsg>

  const filterTabs: { key: VocabFilter; label: string; count?: number }[] = [
    { key: 'all', label: 'All', count: terms.length + pending.length },
    { key: 'pending', label: 'Pending Review', count: pending.length },
    { key: 'approved', label: 'Approved', count: terms.length },
  ]

  return (
    <div className="space-y-5">
      {/* Filter bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-1 bg-surface-container-low rounded-xl p-1 border border-outline-variant/20">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-surface-container-highest text-on-surface shadow-sm'
                  : 'text-secondary hover:text-on-surface'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                  tab.key === 'pending' && tab.count > 0
                    ? 'bg-error/15 text-error'
                    : 'bg-outline-variant/20 text-secondary'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <select
          value={typeFilter}
          onChange={(e) => handleTypeFilter(e.target.value)}
          className="text-sm bg-surface-container border border-outline-variant/30 rounded-lg px-3 py-1.5 text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/40"
        >
          <option value="">All types</option>
          <option value="competency">Capability</option>
          <option value="context_tag">Context</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden border border-outline-variant/30 rounded-xl bg-surface-container-lowest">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-surface-container-low border-b border-outline-variant/30">
              <th className={thCls}>Term</th>
              <th className={thCls}>Type</th>
              <th className={thCls}>Occurrences</th>
              <th className={thCls}>{filter === 'approved' ? 'Definition' : 'Nearest existing'}</th>
              <th className={thCls}>First seen</th>
              <th className={`${thCls} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/20">
            {(filter === 'all' || filter === 'pending') && pending.map((c) => (
              <CandidateRow key={c.id} candidate={c} terms={terms} />
            ))}
            {(filter === 'all' || filter === 'approved') && terms.map((t) => (
              <TermRow key={t.id} term={t} terms={terms} />
            ))}
            {(filter === 'pending' && pending.length === 0) && (
              <tr><td colSpan={6} className="py-16 text-center text-sm text-secondary">No pending candidates. All caught up.</td></tr>
            )}
            {(filter === 'approved' && terms.length === 0) && (
              <tr><td colSpan={6} className="py-16 text-center text-sm text-secondary">No approved terms yet.</td></tr>
            )}
            {(filter === 'all' && terms.length === 0 && pending.length === 0) && (
              <tr><td colSpan={6} className="py-16 text-center text-sm text-secondary">No vocabulary entries found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AutoLinkedPanel />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared micro-components
// ---------------------------------------------------------------------------

function SummaryCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-surface-container-lowest border border-outline-variant/20 p-6 rounded-xl shadow-sm">
      <p className="text-[10px] font-semibold text-secondary tracking-widest uppercase mb-2">{label}</p>
      <p className="text-4xl font-headline text-on-surface">{value}</p>
      {sub && <p className="text-xs text-secondary mt-1">{sub}</p>}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'active'
      ? 'bg-green-50 text-green-800 border-green-100'
      : 'bg-amber-50 text-amber-800 border-amber-100'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center py-16">
      <span className="animate-spin w-5 h-5 border-2 border-primary-container/30 border-t-primary-container rounded-full inline-block" />
    </div>
  )
}

function ErrorMsg({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4 bg-error-container/20 border-l-4 border-error rounded text-sm text-red-800">
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Admin page shell
// ---------------------------------------------------------------------------

const TABS: { key: AdminTab; label: string }[] = [
  { key: 'users', label: 'Users' },
  { key: 'invites', label: 'Invites' },
  { key: 'effectiveness', label: 'Effectiveness' },
  { key: 'vocabulary', label: 'Vocabulary' },
]

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users')
  const [pendingVocab, setPendingVocab] = useState(0)

  return (
    <div className="min-h-screen bg-surface-container-low flex">
      <Sidebar />

      <main className="flex-1 ml-16 lg:ml-64 min-h-screen">
        <header className="sticky top-0 z-40 bg-surface-bright/80 backdrop-blur-xl border-b border-outline-variant/10">
          <div className="max-w-[1280px] mx-auto px-12 h-20 flex items-center justify-between">
            <h2 className="font-headline text-2xl font-light text-on-surface">Admin Console</h2>
            <nav className="flex items-center gap-8">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    'relative pb-2 text-sm font-medium transition-colors',
                    activeTab === tab.key
                      ? 'text-primary-container border-b-2 border-primary-container'
                      : 'text-secondary hover:text-on-surface',
                  ].join(' ')}
                >
                  {tab.label}
                  {tab.key === 'vocabulary' && pendingVocab > 0 && (
                    <span className="absolute -top-1.5 -right-3 w-4 h-4 bg-primary-container text-on-primary text-[9px] font-bold rounded-full flex items-center justify-center">
                      {pendingVocab > 9 ? '9+' : pendingVocab}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </header>

        <div className="max-w-[1280px] mx-auto px-12 py-10">
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'invites' && <InvitesTab />}
          {activeTab === 'effectiveness' && <EffectivenessTab />}
          {activeTab === 'vocabulary' && <VocabularyTab onPendingCount={setPendingVocab} />}
        </div>
      </main>
    </div>
  )
}
